import { create } from 'zustand';
import type { User } from 'firebase/auth';
import type { FirebaseAuthError, UserProfile, AccessState } from '@/types';
import { signUp, signIn, logOut, resetPassword, auth } from '@/lib/firebase/auth';
import { 
  createUserProfile, 
  getUserProfile, 
  updateUserProfile, 
  checkUserAccess
} from '@/lib/firebase/userProfiles';
import { onAuthStateChanged } from 'firebase/auth';

interface AuthState {
  user: User | null;
  userProfile: UserProfile | null;
  accessState: AccessState | null;
  loading: boolean;
  error: string | null;
  initialized: boolean;
}

interface AuthActions {
  signUp: (email: string, password: string, displayName: string) => Promise<void>;
  signIn: (email: string, password: string) => Promise<void>;
  signOut: () => Promise<void>;
  resetPassword: (email: string) => Promise<void>;
  clearError: () => void;
  setLoading: (loading: boolean) => void;
  setUser: (user: User | null) => void;
  initialize: () => void;
  fetchUserProfile: () => Promise<void>;
  updateUserProfile: (data: Partial<UserProfile>) => Promise<void>;
  checkAccessState: () => Promise<AccessState>;
}

type AuthStore = AuthState & AuthActions;

const useAuthStore = create<AuthStore>((set, get) => ({
  // Initial state
  user: null,
  userProfile: null,
  accessState: null,
  loading: false,
  error: null,
  initialized: false,

  // Initialize auth state listener
  initialize: () => {
    if (get().initialized) return;
    
    // Get current user immediately if available
    const currentUser = auth.currentUser;
    
    set({ 
      initialized: true, 
      user: currentUser,
      loading: false 
    });
    
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      set({ user, loading: false });
      
      // Fetch user profile and access state when user changes
      if (user) {
        try {
          await get().fetchUserProfile();
        } catch (error) {
          console.error('Error fetching user profile on auth change:', error);
        }
      } else {
        // Clear profile data when user signs out
        set({ userProfile: null, accessState: null });
      }
    });
    
    // Return unsubscribe function for cleanup if needed
    return unsubscribe;
  },

  // Sign up with email and password
  signUp: async (email: string, password: string, displayName: string) => {
    try {
      set({ loading: true, error: null });
      
      const userCredential = await signUp({
        email,
        password,
        displayName,
      });
      
      // Create user profile with trial subscription
      const userProfile = await createUserProfile(
        userCredential.user.uid, 
        email, 
        displayName
      );
      
      // Calculate initial access state
      const accessState = await checkUserAccess(userCredential.user.uid);
      
      set({ 
        user: userCredential.user, 
        userProfile,
        accessState,
        loading: false 
      });
    } catch (error: unknown) {
      const authError = error as FirebaseAuthError;
      set({ 
        loading: false, 
        error: authError.message || 'Failed to create account' 
      });
      throw error;
    }
  },

  // Sign in with email and password
  signIn: async (email: string, password: string) => {
    try {
      set({ loading: true, error: null });
      
      const userCredential = await signIn({ email, password });
      
      set({ user: userCredential.user, loading: false });
    } catch (error: unknown) {
      const authError = error as FirebaseAuthError;
      set({ 
        loading: false, 
        error: authError.message || 'Failed to sign in' 
      });
      throw error;
    }
  },

  // Sign out
  signOut: async () => {
    try {
      set({ loading: true, error: null });
      
      await logOut();
      
      set({ 
        user: null, 
        userProfile: null, 
        accessState: null, 
        loading: false 
      });
    } catch (error: unknown) {
      const authError = error as FirebaseAuthError;
      set({ 
        loading: false, 
        error: authError.message || 'Failed to sign out' 
      });
      throw error;
    }
  },

  // Reset password
  resetPassword: async (email: string) => {
    try {
      set({ loading: true, error: null });
      
      await resetPassword(email);
      
      set({ loading: false });
    } catch (error: unknown) {
      const authError = error as FirebaseAuthError;
      set({ 
        loading: false, 
        error: authError.message || 'Failed to send reset email' 
      });
      throw error;
    }
  },

  // Clear error
  clearError: () => {
    set({ error: null });
  },

  // Set loading state
  setLoading: (loading: boolean) => {
    set({ loading });
  },

  // Set user
  setUser: (user: User | null) => {
    set({ user });
  },

  // Fetch user profile and access state
  fetchUserProfile: async () => {
    const { user } = get();
    if (!user) return;

    try {
      set({ loading: true, error: null });
      
      const userProfile = await getUserProfile(user.uid);
      if (userProfile) {
        const accessState = await checkUserAccess(user.uid);
        set({ userProfile, accessState, loading: false });
      } else {
        // User profile doesn't exist, create one
        const newProfile = await createUserProfile(user.uid, user.email!, user.displayName || '');
        const accessState = await checkUserAccess(user.uid);
        set({ userProfile: newProfile, accessState, loading: false });
      }
    } catch (error: unknown) {
      console.error('Error fetching user profile:', error);
      set({ loading: false, error: 'Failed to load user profile' });
      throw error;
    }
  },

  // Update user profile
  updateUserProfile: async (data: Partial<UserProfile>) => {
    const { user } = get();
    if (!user) throw new Error('No authenticated user');

    try {
      set({ loading: true, error: null });
      
      await updateUserProfile(user.uid, data);
      
      // Refresh profile data
      await get().fetchUserProfile();
    } catch (error: unknown) {
      console.error('Error updating user profile:', error);
      set({ loading: false, error: 'Failed to update profile' });
      throw error;
    }
  },

  // Extend user access
  // Check current access state
  checkAccessState: async (): Promise<AccessState> => {
    const { user } = get();
    if (!user) throw new Error('No authenticated user');

    try {
      const accessState = await checkUserAccess(user.uid);
      set({ accessState });
      return accessState;
    } catch (error: unknown) {
      console.error('Error checking access state:', error);
      throw error;
    }
  },
}));

export default useAuthStore;